import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, NavLink } from 'react-router-dom';
import { CalendarClock, Clock, Users, Mountain, ArrowLeft, Loader2, User, ExternalLink, FileVideo, Edit, Save, X, MapPin, Check, X as XIcon, Info, Download } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { formatDate, formatTime, getVideoEmbedUrl, validateVideoUrl } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';
import { useAuth } from '@/contexts/AuthContext';
import { mapboxgl } from '@/components/map/utils/mapboxConfig';
import 'mapbox-gl/dist/mapbox-gl.css';
import { appsAdvApiRouterGetRide, appsAdvApiRouterUpdateRide, appsAdvApiRouterGetTrack, appsAdvApiRouterGetTrackDownloadUrl, RideOut, TrackResponse } from '@/lib/api';
import { RsvpSection } from '@/components/rides/RsvpSection';
import { RideRatingModal } from '@/components/rides/RideRatingModal';
import { TerrainDisplay } from '@/components/terrain';
import { TerrainType } from '@/lib/types/core/terrain';
import { useRsvpStatus } from '@/hooks/useRsvpStatus';
import LocationMap from '@/components/map/LocationMap';
import { reverseGeocode, geocodeAddress } from '@/lib/geocoding';

const RideDetail = () => {
  const { id } = useParams<{ id: string }>();
  const [ride, setRide] = useState<RideOut | null>(null);
  const [track, setTrack] = useState<TrackResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [videoEmbed, setVideoEmbed] = useState<{ url: string, platform: string } | null>(null);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [mapContainerReady, setMapContainerReady] = useState(false);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const waypointMarkersRef = useRef<mapboxgl.Marker[]>([]);
  const waypointPopupRef = useRef<mapboxgl.Popup | null>(null);

  // Callback ref to track when map container is mounted
  const setMapContainerRef = useCallback((node: HTMLDivElement | null) => {
    mapContainerRef.current = node;
    if (node) {
      setMapContainerReady(true);
    }
  }, []);
  const [formData, setFormData] = useState<Omit<RideOut, 'id' | 'created_by' | 'created_at'>>({
    title: '',
    ride_type: 'Street',
    rider_skill: 'Beginner',
    terrain_difficulty: 'Easy',
    meeting_latitude: 0,
    meeting_longitude: 0,
    meeting_address: '',
    meeting_date: '',
    kickstands_up: '',
    description: '',
    gpx_link: '',
    image_url: '',
    video_url: '',
    requires_approval: false,
    max_participants: undefined,
    multi_day: false,
    end_date: undefined,
    end_date_max: undefined,
    dates_flexible: false,
    overnight_plan: undefined,
    meal_plan: undefined,
  });
  const [saving, setSaving] = useState(false);
  const [address, setAddress] = useState('');
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [geocodingStatus, setGeocodingStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [showMap, setShowMap] = useState(false);
  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null);
  const { toast } = useToast();
  const { user } = useAuth();
  const { is_rsvped, hasRated, refresh: refreshRsvpStatus } = useRsvpStatus(id);
  
  useEffect(() => {
    const fetchRide = async () => {
      try {
        setLoading(true);
        
        if (!id) {
          throw new Error('Ride ID is missing');
        }

        const response = await appsAdvApiRouterGetRide(id);
        const rideData = response.data;
        setRide(rideData);

        setFormData({
          title: rideData.title,
          ride_type: rideData.ride_type,
          rider_skill: rideData.rider_skill,
          terrain_difficulty: rideData.terrain_difficulty,
          meeting_latitude: rideData.meeting_latitude,
          meeting_longitude: rideData.meeting_longitude,
          meeting_address: rideData.meeting_address || '',
          meeting_date: rideData.meeting_date,
          kickstands_up: rideData.kickstands_up,
          description: rideData.description,
          gpx_link: rideData.gpx_link || '',
          image_url: rideData.image_url || '',
          video_url: rideData.video_url || '',
          requires_approval: rideData.requires_approval || false,
          max_participants: rideData.max_participants,
          multi_day: rideData.multi_day || false,
          end_date: rideData.end_date || undefined,
          end_date_max: rideData.end_date_max || undefined,
          dates_flexible: rideData.dates_flexible || false,
          overnight_plan: rideData.overnight_plan || undefined,
          meal_plan: rideData.meal_plan || undefined,
        });
        setAddress(rideData.meeting_address || '');
        
        if (rideData.video_url) {
          const validation = validateVideoUrl(rideData.video_url);
          if (validation.valid && validation.platform) {
            setVideoEmbed({
              url: getVideoEmbedUrl(rideData.video_url, validation.platform),
              platform: validation.platform
            });
          }
        }

        // Fetch track data (separate request, may 404 if no track exists)
        try {
          const trackResponse = await appsAdvApiRouterGetTrack(id);
          setTrack(trackResponse.data);
        } catch {
          // Track doesn't exist for this ride - that's okay
          setTrack(null);
        }
      } catch (error: any) {
        console.error('Error fetching ride:', error);
        setError(error.message);
        toast({
          title: "Failed to load ride details",
          description: error.message,
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
    };

    fetchRide();
  }, [id, toast]);
  
  useEffect(() => {
    if (!ride || !mapboxgl.accessToken || mapLoaded || !mapContainerReady) return;

    const initializeMap = async () => {
      try {
        if (!mapContainerRef.current) {
          return;
        }

        const coordinates: [number, number] = [ride.meeting_longitude, ride.meeting_latitude];

        const map = new mapboxgl.Map({
          container: mapContainerRef.current,
          style: 'mapbox://styles/mapbox/dark-v11',
          center: coordinates,
          zoom: 12
        });

        mapRef.current = map;

        map.addControl(new mapboxgl.NavigationControl(), 'top-right');

        map.on('load', () => {

          const el = document.createElement('div');

          const markerEl = document.createElement('div');
          markerEl.className = 'flex items-center justify-center w-8 h-8';
          markerEl.innerHTML = `
            <div class="absolute animate-ping w-6 h-6 rounded-full bg-adv-orange opacity-30"></div>
            <div class="relative flex items-center justify-center w-6 h-6 rounded-full bg-adv-orange text-white">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0z"></path>
                <path d="M10 0v8a2 2 0 0 0 4 0V0"></path>
              </svg>
            </div>
          `;
          el.appendChild(markerEl);

          new mapboxgl.Marker(el)
            .setLngLat(coordinates)
            .addTo(map);

          new mapboxgl.Popup({ offset: 25 })
            .setLngLat(coordinates)
            .setHTML('<h3 class="font-bold">Meeting Point</h3><p>Kickstands up at ' + formatTime(ride.kickstands_up) + '</p>')
            .addTo(map);

          setMapLoaded(true);
        });
      } catch (error) {
        console.error('Map initialization error:', error);
      }
    };

    initializeMap();

    // Cleanup on unmount
    return () => {
      if (mapRef.current) {
        // Clean up waypoint markers
        waypointMarkersRef.current.forEach(marker => marker.remove());
        waypointMarkersRef.current = [];
        if (waypointPopupRef.current) {
          waypointPopupRef.current.remove();
          waypointPopupRef.current = null;
        }
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapContainerReady]);

  // Add track layer and waypoint markers when map and track data are available
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded || !track || track.status !== 'completed' || !track.geojson) return;

    const geojson = track.geojson as unknown as GeoJSON.FeatureCollection;

    // Add track source and layer
    if (!map.getSource('track')) {
      map.addSource('track', {
        type: 'geojson',
        data: geojson
      });

      map.addLayer({
        id: 'track-line',
        type: 'line',
        source: 'track',
        filter: ['==', ['get', 'type'], 'track'],
        paint: {
          'line-color': '#ea580c', // Orange-600
          'line-width': 4,
          'line-opacity': 0.8,
        },
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
      });
    }

    // Clear existing waypoint markers
    waypointMarkersRef.current.forEach(marker => marker.remove());
    waypointMarkersRef.current = [];

    // Add waypoint markers
    const waypoints = geojson.features.filter(
      (f: GeoJSON.Feature) => f.properties?.type === 'waypoint'
    );

    waypoints.forEach((waypoint: GeoJSON.Feature, index: number) => {
      if (waypoint.geometry.type !== 'Point') return;
      const coords = waypoint.geometry.coordinates as [number, number];

      const el = document.createElement('div');
      el.className = 'w-6 h-6 bg-white border-2 border-orange-500 rounded-full flex items-center justify-center shadow-sm cursor-pointer';
      el.innerHTML = `<span class="text-xs font-bold text-orange-600">${index + 1}</span>`;

      const marker = new mapboxgl.Marker(el)
        .setLngLat(coords)
        .addTo(map);

      el.addEventListener('click', (e) => {
        e.stopPropagation();
        // Close existing popup
        if (waypointPopupRef.current) {
          waypointPopupRef.current.remove();
        }
        // Create and show popup
        waypointPopupRef.current = new mapboxgl.Popup({ closeButton: true, closeOnClick: false, anchor: 'bottom' })
          .setLngLat(coords)
          .setHTML(`<div class="text-sm font-medium">${waypoint.properties?.name || 'Waypoint'}</div>`)
          .addTo(map);
      });

      waypointMarkersRef.current.push(marker);
    });

    // Fit map to track bounds
    const trackFeature = geojson.features.find(
      (f: GeoJSON.Feature) => f.properties?.type === 'track'
    );

    if (trackFeature && trackFeature.geometry.type === 'LineString') {
      const coordinates = trackFeature.geometry.coordinates as [number, number][];
      if (coordinates.length > 0) {
        const bounds = coordinates.reduce(
          (bounds, coord) => bounds.extend(coord as mapboxgl.LngLatLike),
          new mapboxgl.LngLatBounds(coordinates[0], coordinates[0])
        );
        map.fitBounds(bounds, { padding: 50 });
      }
    }

    // Cleanup track source on unmount or track change
    return () => {
      if (map.getLayer('track-line')) {
        map.removeLayer('track-line');
      }
      if (map.getSource('track')) {
        map.removeSource('track');
      }
      waypointMarkersRef.current.forEach(marker => marker.remove());
      waypointMarkersRef.current = [];
    };
  }, [mapLoaded, track]);

  // Check if rating modal should be shown
  useEffect(() => {
    if (!ride || !user || !is_rsvped || hasRated) return;

    const now = new Date();
    const todayMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    const dateString = ride.end_date ?? ride.meeting_date;
    const [year, month, day] = dateString.split('-').map(Number);
    const rideCutoff = new Date(year, month - 1, day + 1);

    // Show rating modal if ride has passed (we're on or after the day after the ride) and user hasn't rated yet
    if (todayMidnight >= rideCutoff) {
      setShowRatingModal(true);
    }
  }, [ride, user, is_rsvped, hasRated]);

  const handleRatingComplete = () => {
    refreshRsvpStatus();
  };

  const handleDownload = async () => {
    if (!id) return;

    setDownloading(true);
    try {
      const response = await appsAdvApiRouterGetTrackDownloadUrl(id);
      const { url, filename } = response.data;

      // Trigger download using temporary anchor element
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setShowDownloadModal(false);
      toast({
        title: "Download started",
        description: `Downloading ${filename}`,
      });
    } catch (error: any) {
      console.error('Download error:', error);
      toast({
        title: "Download failed",
        description: error.message || "Unable to download GPX file",
        variant: "destructive"
      });
    } finally {
      setDownloading(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    if (ride) {
      setFormData({
        title: ride.title,
        ride_type: ride.ride_type,
        rider_skill: ride.rider_skill,
        terrain_difficulty: ride.terrain_difficulty,
        meeting_latitude: ride.meeting_latitude,
        meeting_longitude: ride.meeting_longitude,
        meeting_address: ride.meeting_address || '',
        meeting_date: ride.meeting_date,
        kickstands_up: ride.kickstands_up,
        description: ride.description,
        gpx_link: ride.gpx_link || '',
        image_url: ride.image_url || '',
        video_url: ride.video_url || '',
        requires_approval: ride.requires_approval || false,
        max_participants: ride.max_participants,
        multi_day: ride.multi_day || false,
        end_date: ride.end_date || undefined,
        end_date_max: ride.end_date_max || undefined,
        dates_flexible: ride.dates_flexible || false,
        overnight_plan: ride.overnight_plan || undefined,
        meal_plan: ride.meal_plan || undefined,
      });
      setAddress(ride.meeting_address || '');
    }
    setIsEditing(false);
    setShowMap(false);
    setGeocodingStatus('idle');
  };

  const handleSave = async () => {
    if (!id || !ride) return;

    try {
      setSaving(true);
      const response = await appsAdvApiRouterUpdateRide(id, formData);
      const updatedRide = response.data;
      setRide(updatedRide);
      setIsEditing(false);

      if (updatedRide.video_url) {
        const validation = validateVideoUrl(updatedRide.video_url);
        if (validation.valid && validation.platform) {
          setVideoEmbed({
            url: getVideoEmbedUrl(updatedRide.video_url, validation.platform),
            platform: validation.platform
          });
        }
      } else {
        setVideoEmbed(null);
      }

      toast({
        title: "Ride updated",
        description: "Your ride has been successfully updated.",
      });
    } catch (error: any) {
      console.error('Error updating ride:', error);
      toast({
        title: "Failed to update ride",
        description: error.message || "An error occurred while updating the ride.",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const isCreator = user && ride && user.id === ride.created_by.id;

  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newAddress = e.target.value;
    setAddress(newAddress);
    setFormData({ ...formData, meeting_address: newAddress });
    setGeocodingStatus('idle');

    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    const timer = setTimeout(() => {
      if (newAddress.trim()) {
        performGeocoding(newAddress);
      }
    }, 1000);

    setDebounceTimer(timer);
  };

  const performGeocoding = async (addressToGeocode: string) => {
    setIsGeocoding(true);
    setGeocodingStatus('idle');

    try {
      const results = await geocodeAddress(addressToGeocode, { limit: 1 });
      
      if (results.length > 0) {
        const [lng, lat] = results[0].coordinates;
        setFormData(prev => ({
          ...prev,
          meeting_latitude: lat,
          meeting_longitude: lng,
          meeting_address: addressToGeocode
        }));
        setGeocodingStatus('success');
      } else {
        setGeocodingStatus('error');
        toast({
          title: "Address not found",
          description: "Could not find coordinates for this address.",
          variant: "destructive"
        });
      }
    } catch (error) {
      setGeocodingStatus('error');
      toast({
        title: "Geocoding failed",
        description: "Failed to convert address to coordinates.",
        variant: "destructive"
      });
    } finally {
      setIsGeocoding(false);
    }
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast({
        title: "Geolocation unavailable",
        description: "Your browser doesn't support geolocation.",
        variant: "destructive"
      });
      return;
    }
    
    setIsGeocoding(true);
    setGeocodingStatus('idle');
    
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        
        try {
          const results = await reverseGeocode([longitude, latitude], { limit: 1 });
          if (results.length > 0) {
            const newAddress = results[0].placeName;
            setAddress(newAddress);
            setFormData(prev => ({
              ...prev,
              meeting_latitude: latitude,
              meeting_longitude: longitude,
              meeting_address: newAddress
            }));
          } else {
            setFormData(prev => ({
              ...prev,
              meeting_latitude: latitude,
              meeting_longitude: longitude
            }));
          }
        } catch (error) {
          console.error("Failed to get address:", error);
          setFormData(prev => ({
            ...prev,
            meeting_latitude: latitude,
            meeting_longitude: longitude
          }));
        }
        
        setGeocodingStatus('success');
        setIsGeocoding(false);
      },
      (error) => {
        setIsGeocoding(false);
        setGeocodingStatus('error');
        toast({
          title: "Location error",
          description: error.message,
          variant: "destructive"
        });
      }
    );
  };

  const handleLocationChange = useCallback((lat: number, lng: number) => {
    setFormData(prev => ({
      ...prev,
      meeting_latitude: lat,
      meeting_longitude: lng
    }));
  }, []);

  const handleMapAddressChange = useCallback((newAddress: string) => {
    setAddress(newAddress);
    setFormData(prev => ({
      ...prev,
      meeting_address: newAddress
    }));
    setGeocodingStatus('success');
  }, []);

  useEffect(() => {
    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, [debounceTimer]);

  
  
  if (loading) {
    return (
      <div className="bg-adv-dark min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 size={48} className="text-adv-orange animate-spin mb-4 mx-auto" />
          <p className="text-white text-lg">Loading ride details...</p>
        </div>
      </div>
    );
  }
  
  if (error || !ride) {
    return (
      <div className="bg-adv-dark min-h-screen pt-28">
        <div className="container-custom">
          <div className="glass-card rounded-lg p-12 text-center max-w-2xl mx-auto">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-adv-orange mb-4 mx-auto"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <h1 className="heading-lg text-white mb-4">Ride Not Found</h1>
            <p className="text-adv-lightGray mb-6">
              {error || "We couldn't find the ride you're looking for."}
            </p>
            <Button className="btn-primary">
              <NavLink to="/ride-hub" className="flex items-center">
                <ArrowLeft size={18} className="mr-2" /> Back to Rides
              </NavLink>
            </Button>
          </div>
        </div>
      </div>
    );
  }
  
  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'Easy':
        return 'bg-green-500';
      case 'Moderate':
        return 'bg-blue-500';
      case 'Hard':
        return 'bg-adv-orange';
      default:
        return 'bg-gray-500';
    }
  };

  
  return (
    <div className="bg-adv-dark min-h-screen pt-28 pb-16">
      <div className="container-custom">
        <div className="mb-6">
          <NavLink to="/ride-hub" className="text-adv-lightGray hover:text-white flex items-center">
            <ArrowLeft size={16} className="mr-1" /> Back to Rides
          </NavLink>
        </div>
        
        <header className="mb-8">
          <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
            {isEditing ? (
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="flex-1 max-w-md bg-adv-charcoal text-white border-adv-mediumGray"
                placeholder="Ride title"
              />
            ) : (
              <h1 className="heading-lg">{ride.title}</h1>
            )}
            {isEditing ? (
              <Select
                value={formData.terrain_difficulty}
                onValueChange={(value) => setFormData({ ...formData, terrain_difficulty: value as 'Easy' | 'Moderate' | 'Hard' })}
              >
                <SelectTrigger className="w-32 bg-adv-charcoal text-white border-adv-mediumGray">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Easy">Easy</SelectItem>
                  <SelectItem value="Moderate">Moderate</SelectItem>
                  <SelectItem value="Hard">Hard</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <div className={`text-sm font-medium px-3 py-1 rounded-full ${getDifficultyColor(ride.terrain_difficulty)}`}>
                {ride.terrain_difficulty}
              </div>
            )}
            {isCreator && !isEditing && (
              <Button
                onClick={handleEdit}
                className="btn-primary"
              >
                <Edit size={18} className="mr-2" />
                Edit Ride
              </Button>
            )}
            {isCreator && isEditing && (
              <div className="flex gap-2">
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="btn-primary"
                >
                  <Save size={18} className="mr-2" />
                  {saving ? 'Saving...' : 'Save'}
                </Button>
                <Button
                  onClick={handleCancel}
                  disabled={saving}
                  variant="outline"
                  className="border-adv-mediumGray hover:bg-adv-charcoal"
                >
                  <X size={18} className="mr-2" />
                  Cancel
                </Button>
              </div>
            )}
          </div>
          
          <div className="flex flex-wrap gap-6 text-adv-lightGray">
            <div className="flex items-center">
              <CalendarClock size={18} className="mr-2 text-adv-orange" /> 
              {isEditing ? (
                <Input
                  type="date"
                  value={formData.meeting_date}
                  onChange={(e) => setFormData({ ...formData, meeting_date: e.target.value })}
                  className="w-auto bg-adv-charcoal text-white border-adv-mediumGray"
                />
              ) : (
                <>
                  {ride.multi_day && ride.end_date ? (
                    <>
                      {formatDate(ride.meeting_date, false)} - {ride.dates_flexible && ride.end_date_max ? (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="cursor-help">~{formatDate(ride.end_date, false)} - {formatDate(ride.end_date_max, false)}</span>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Dates are flexible</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ) : (
                        formatDate(ride.end_date, false)
                      )}
                    </>
                  ) : (
                    <>
                      {formatDate(ride.meeting_date, false)}
                      {ride.multi_day && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info size={16} className="ml-2 text-adv-orange cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>This is a multi-day ride</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
            <div className="flex items-center">
              <Clock size={18} className="mr-2 text-adv-orange" /> 
              {isEditing ? (
                <Input
                  type="time"
                  value={formData.kickstands_up}
                  onChange={(e) => setFormData({ ...formData, kickstands_up: e.target.value })}
                  className="w-auto bg-adv-charcoal text-white border-adv-mediumGray"
                />
              ) : (
                `Kickstands Up: ${formatTime(ride.kickstands_up)}`
              )}
            </div>
            <div className="flex items-center">
              <Users size={18} className="mr-2 text-adv-orange" /> 
              {isEditing ? (
                <Select
                  value={formData.rider_skill}
                  onValueChange={(value) => setFormData({ ...formData, rider_skill: value as 'Beginner' | 'Intermediate' | 'Advanced' })}
                >
                  <SelectTrigger className="w-36 bg-adv-charcoal text-white border-adv-mediumGray">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Beginner">Beginner</SelectItem>
                    <SelectItem value="Intermediate">Intermediate</SelectItem>
                    <SelectItem value="Advanced">Advanced</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                `${ride.rider_skill} riders`
              )}
            </div>
            <div className="flex items-center">
              <Mountain size={18} className="mr-2 text-adv-orange" />
              {isEditing ? (
                <Select
                  value={formData.ride_type}
                  onValueChange={(value) => setFormData({ ...formData, ride_type: value as 'Street' | 'Seeking Off-road' | 'Fully Off-road' })}
                >
                  <SelectTrigger className="w-40 bg-adv-charcoal text-white border-adv-mediumGray">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Street">Street</SelectItem>
                    <SelectItem value="Seeking Off-road">Seeking Off-road</SelectItem>
                    <SelectItem value="Fully Off-road">Fully Off-road</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                ride.ride_type
              )}
            </div>
            {!isEditing && ride.expected_terrain && ride.expected_terrain.length > 0 && (
              <div className="flex items-center">
                <TerrainDisplay terrains={ride.expected_terrain as TerrainType[]} />
              </div>
            )}
            {isEditing ? (
              <div className="flex items-center w-full">
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  width="18" 
                  height="18" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                  className="mr-2 text-adv-orange"
                >
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                  <circle cx="12" cy="10" r="3"></circle>
                </svg>
                <div className="flex-1 relative">
                  <Input
                    value={address}
                    onChange={handleAddressChange}
                    placeholder="Enter address (e.g., 123 Main St, Nashville, TN)"
                    className="w-full px-4 py-2.5 pr-10 bg-adv-charcoal text-white border-adv-mediumGray rounded-md focus:ring-2 focus:ring-adv-orange focus:border-transparent outline-none transition-all"
                  />
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    {isGeocoding && (
                      <Loader2 size={16} className="animate-spin text-adv-orange" />
                    )}
                    {geocodingStatus === 'success' && !isGeocoding && (
                      <Check size={16} className="text-green-500" />
                    )}
                    {geocodingStatus === 'error' && !isGeocoding && (
                      <XIcon size={16} className="text-red-500" />
                    )}
                  </div>
                </div>
                <div className="flex gap-2 ml-2">
                  <Button
                    type="button"
                    onClick={getCurrentLocation}
                    disabled={isGeocoding}
                    className="whitespace-nowrap hover:bg-adv-mediumGray"
                  >
                    {isGeocoding ? (
                      <>
                        <Loader2 size={16} className="mr-2 animate-spin" />
                        Getting Location...
                      </>
                    ) : (
                      <>
                        <MapPin size={16} className="mr-2" />
                        Use Current Location
                      </>
                    )}
                  </Button>
                  <Button
                    type="button"
                    onClick={() => setShowMap(!showMap)}
                    variant="outline"
                    className="whitespace-nowrap border-adv-mediumGray text-adv-lightGray hover:bg-adv-darkGray"
                  >
                    {showMap ? 'Hide Map' : 'Show Map'}
                  </Button>
                </div>
              </div>
            ) : (
              ride.meeting_address && (
                <a 
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(ride.meeting_address)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center transition-colors"
                >
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    width="18" 
                    height="18" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                    className="mr-2 text-adv-orange"
                  >
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                    <circle cx="12" cy="10" r="3"></circle>
                  </svg>
                  {ride.meeting_address}
                </a>
              )
            )}
          </div>
          {!isEditing && ride.multi_day && (ride.overnight_plan || ride.meal_plan) && (
            <div className="flex flex-wrap gap-6 text-adv-lightGray mt-2">
              {ride.overnight_plan && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center cursor-help">
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2 text-adv-orange"><path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z"/></svg>
                        {ride.overnight_plan === 'camping' ? 'Camping' : ride.overnight_plan === 'hotel' ? 'Hotel' : 'Mixed'}
                      </div>
                    </TooltipTrigger>
                    <TooltipContent><p>Overnight Plan</p></TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              {ride.meal_plan && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center cursor-help">
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2 text-adv-orange"><path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2"/><path d="M7 2v20"/><path d="M21 15V2v0a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7"/></svg>
                        {ride.meal_plan === 'bring_your_own' ? 'Bring Your Own' : ride.meal_plan === 'restaurants' ? 'Restaurants' : 'Mixed'}
                      </div>
                    </TooltipTrigger>
                    <TooltipContent><p>Meal Plan</p></TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          )}
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            {isEditing ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-white mb-2">Image URL</label>
                  <Input
                    value={formData.image_url || ''}
                    onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                    placeholder="Image URL"
                    className="w-full bg-adv-charcoal text-white border-adv-mediumGray"
                  />
                </div>
                {formData.image_url && (
                  <div className="rounded-lg overflow-hidden">
                    <img 
                      src={formData.image_url} 
                      alt="Preview" 
                      className="w-full h-auto object-cover"
                    />
                  </div>
                )}
              </div>
            ) : ride.image_url ? (
              <div className="rounded-lg overflow-hidden">
                <img 
                  src={ride.image_url} 
                  alt={ride.title} 
                  className="w-full h-auto object-cover"
                />
              </div>
            ) : (
              <div className="h-48 bg-adv-charcoal rounded-lg flex items-center justify-center">
                <Mountain size={64} className="text-adv-mediumGray" />
              </div>
            )}
            
            <div className="glass-card rounded-lg p-6">
              <h2 className="text-xl font-bold mb-4">Ride Description</h2>
              {isEditing ? (
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full bg-adv-charcoal text-white border-adv-mediumGray min-h-[200px]"
                  placeholder="Ride description"
                />
              ) : (
                <div className="text-adv-lightGray whitespace-pre-wrap">
                  {ride.description}
                </div>
              )}
            </div>
            
            <div className="glass-card rounded-lg p-6">
              <h2 className="text-xl font-bold mb-4">Meeting Location</h2>
              {isEditing && showMap && (
                <div className="mb-4">
                  <LocationMap
                    latitude={formData.meeting_latitude}
                    longitude={formData.meeting_longitude}
                    onLocationChange={handleLocationChange}
                    onAddressChange={handleMapAddressChange}
                    height="300px"
                    className="rounded-lg border border-adv-darkGray"
                  />
                </div>
              )}
              <div className="relative">
                <div ref={setMapContainerRef} className="h-80 rounded-lg overflow-hidden"></div>
                {/* Processing status badge - show for both 'pending' and 'processing' states */}
                {track && (track.status === 'pending' || track.status === 'processing') && (
                  <div className="absolute top-2 left-2 bg-amber-100 text-amber-800 px-2 py-1 rounded text-sm flex items-center gap-1">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Processing track...
                  </div>
                )}
                {/* Error badge for failed track processing */}
                {track && track.status === 'failed' && (
                  <div className="absolute top-2 left-2 bg-red-100 text-red-800 px-2 py-1 rounded text-sm flex items-center gap-1">
                    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    Track processing failed
                  </div>
                )}
              </div>
              {/* Disclaimer text when track exists */}
              {track && track.status === 'completed' && (
                <div className="mt-4 space-y-3">
                  <p className="text-xs text-gray-500 italic">
                    This route was provided by the ride organizer and has not been verified by Exodus.
                    Exodus makes no representations about the accuracy, legality, or safety of this route.
                    Riders are responsible for verifying road access and conditions.
                  </p>
                  {track.can_download && (
                    <Button
                      onClick={() => setShowDownloadModal(true)}
                      className="flex items-center gap-2 bg-orange-600 text-white hover:bg-orange-700"
                    >
                      <Download className="h-5 w-5" />
                      Download GPX
                    </Button>
                  )}
                </div>
              )}
            </div>
            
            {(videoEmbed || isEditing) && (
              <div className="glass-card rounded-lg p-6">
                <h2 className="text-xl font-bold mb-4 flex items-center">
                  <FileVideo size={20} className="mr-2 text-adv-orange" />
                  Ride Preview Video
                </h2>
                {isEditing ? (
                  <div>
                    <Input
                      value={formData.video_url || ''}
                      onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                      placeholder="Video URL (YouTube, Vimeo, etc.)"
                      className="w-full bg-adv-charcoal border-adv-mediumGray mb-4"
                    />
                    {formData.video_url && (() => {
                      const validation = validateVideoUrl(formData.video_url);
                      if (validation.valid && validation.platform) {
                        const embedUrl = getVideoEmbedUrl(formData.video_url, validation.platform);
                        return (
                          <div className="aspect-video rounded-lg overflow-hidden bg-black">
                            {validation.platform === 'youtube' ? (
                              <iframe
                                src={embedUrl}
                                title="Ride Preview"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                                className="w-full h-full"
                              ></iframe>
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <a 
                                  href={formData.video_url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="btn-primary"
                                >
                                  <ExternalLink size={18} className="mr-2" />
                                  View Video on {validation.platform.charAt(0).toUpperCase() + validation.platform.slice(1)}
                                </a>
                              </div>
                            )}
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>
                ) : videoEmbed ? (
                  <div className="aspect-video rounded-lg overflow-hidden bg-black">
                    {videoEmbed.platform === 'youtube' ? (
                      <iframe
                        src={videoEmbed.url}
                        title="Ride Preview"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        className="w-full h-full"
                      ></iframe>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <a 
                          href={ride.video_url || '#'} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="btn-primary"
                        >
                          <ExternalLink size={18} className="mr-2" />
                          View Video on {videoEmbed.platform.charAt(0).toUpperCase() + videoEmbed.platform.slice(1)}
                        </a>
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            )}
          </div>
          
          <div className="space-y-6">
            <div className="glass-card rounded-lg p-6">
              <h2 className="text-xl font-bold mb-4">Ride Organizer</h2>
              {ride.created_by ? (
                <div className="flex items-center">
                  <div className="h-12 w-12 rounded-full bg-adv-darkGray flex items-center justify-center mr-3">
                    {ride.created_by.avatar_url ? (
                      <img 
                        src={ride.created_by.avatar_url} 
                        alt={ride.created_by.username} 
                        className="h-full w-full rounded-full object-cover"
                      />
                    ) : (
                      <User size={24} className="text-adv-lightGray" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium">{ride.created_by.username}</p>
                    <p className="text-sm text-adv-lightGray">
                      {ride.created_by.first_name} {ride.created_by.last_name}
                    </p>
                  </div>
                </div>
              ) : (
                <p className="text-adv-lightGray">Unknown organizer</p>
              )}
            </div>
            
            {(ride.gpx_link || isEditing) && (
              <div className="glass-card rounded-lg p-6">
                <h2 className="text-xl font-bold mb-4">Route Information</h2>
                {isEditing ? (
                  <div>
                    <Input
                      value={formData.gpx_link || ''}
                      onChange={(e) => setFormData({ ...formData, gpx_link: e.target.value })}
                      placeholder="GPX Link URL"
                      className="w-full bg-adv-charcoal border-adv-mediumGray mb-4"
                    />
                    {formData.gpx_link && (
                      <a 
                        href={formData.gpx_link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="btn-primary w-full flex items-center justify-center"
                      >
                        <ExternalLink size={18} className="mr-2" />
                        View Route
                      </a>
                    )}
                  </div>
                ) : (
                  <>
                    <p className="text-adv-lightGray mb-4">
                      The organizer has shared a route for this ride.
                    </p>
                    <a 
                      href={ride.gpx_link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="btn-primary w-full flex items-center justify-center"
                    >
                      <ExternalLink size={18} className="mr-2" />
                      View Route
                    </a>
                  </>
                )}
              </div>
            )}

            {isEditing && (
              <div className="glass-card rounded-lg p-6">
                <h2 className="text-xl font-bold mb-4">Ride Settings</h2>
                <div className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="requires_approval"
                      checked={formData.requires_approval}
                      onCheckedChange={(checked) => setFormData({ ...formData, requires_approval: checked as boolean })}
                      className="border-adv-mediumGray"
                    />
                    <Label htmlFor="requires_approval" className="cursor-pointer">
                      Requires approval for RSVP
                    </Label>
                  </div>
                  <div>
                    <Label htmlFor="max_participants" className="mb-2 block">
                      Maximum Participants (optional)
                    </Label>
                    <Input
                      id="max_participants"
                      type="number"
                      min="1"
                      value={formData.max_participants || ''}
                      onChange={(e) => setFormData({ ...formData, max_participants: e.target.value ? parseInt(e.target.value) : undefined })}
                      placeholder="Leave empty for unlimited"
                      className="w-full bg-adv-charcoal text-white border-adv-mediumGray"
                    />
                  </div>
                  <div>
                    <Label className="mb-2 block">Duration</Label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, multi_day: false, end_date: undefined, end_date_max: undefined, dates_flexible: false, overnight_plan: undefined, meal_plan: undefined })}
                        className={`flex-1 px-4 py-2 rounded-md text-sm font-medium border transition-all ${
                          !formData.multi_day
                            ? 'bg-adv-orange text-white border-adv-orange'
                            : 'bg-adv-charcoal text-adv-lightGray border-adv-mediumGray hover:border-adv-orange'
                        }`}
                      >
                        Single Day
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, multi_day: true })}
                        className={`flex-1 px-4 py-2 rounded-md text-sm font-medium border transition-all ${
                          formData.multi_day
                            ? 'bg-adv-orange text-white border-adv-orange'
                            : 'bg-adv-charcoal text-adv-lightGray border-adv-mediumGray hover:border-adv-orange'
                        }`}
                      >
                        Multi-Day
                      </button>
                    </div>
                  </div>
                  {formData.multi_day && (
                    <div className="space-y-4 pl-3 border-l-2 border-adv-orange/30">
                      <div>
                        <Label className="mb-1 block">End Date *</Label>
                        <Input
                          type="date"
                          value={formData.end_date || ''}
                          onChange={(e) => setFormData({ ...formData, end_date: e.target.value || undefined })}
                          className="w-full bg-adv-charcoal text-white border-adv-mediumGray"
                        />
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="edit_dates_flexible"
                          checked={formData.dates_flexible}
                          onCheckedChange={(checked) => setFormData({ ...formData, dates_flexible: checked as boolean, end_date_max: checked ? formData.end_date_max : undefined })}
                          className="border-adv-mediumGray"
                        />
                        <Label htmlFor="edit_dates_flexible" className="cursor-pointer">Dates are flexible</Label>
                      </div>
                      {formData.dates_flexible && (
                        <div>
                          <Label className="mb-1 block">End Date (Latest)</Label>
                          <Input
                            type="date"
                            value={formData.end_date_max || ''}
                            onChange={(e) => setFormData({ ...formData, end_date_max: e.target.value || undefined })}
                            className="w-full bg-adv-charcoal text-white border-adv-mediumGray"
                          />
                        </div>
                      )}
                      <div>
                        <Label className="mb-1 block">Overnight Plan</Label>
                        <select
                          value={formData.overnight_plan || ''}
                          onChange={(e) => setFormData({ ...formData, overnight_plan: e.target.value || undefined })}
                          className="w-full px-4 py-2.5 bg-adv-charcoal text-white border border-adv-mediumGray rounded-md focus:ring-2 focus:ring-adv-orange focus:border-transparent outline-none transition-all"
                        >
                          <option value="">Select...</option>
                          <option value="camping">Camping</option>
                          <option value="hotel">Hotel</option>
                          <option value="mixed">Mixed</option>
                        </select>
                      </div>
                      <div>
                        <Label className="mb-1 block">Meal Plan</Label>
                        <select
                          value={formData.meal_plan || ''}
                          onChange={(e) => setFormData({ ...formData, meal_plan: e.target.value || undefined })}
                          className="w-full px-4 py-2.5 bg-adv-charcoal text-white border border-adv-mediumGray rounded-md focus:ring-2 focus:ring-adv-orange focus:border-transparent outline-none transition-all"
                        >
                          <option value="">Select...</option>
                          <option value="bring_your_own">Bring Your Own</option>
                          <option value="restaurants">Restaurants</option>
                          <option value="mixed">Mixed</option>
                        </select>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            <RsvpSection ride={ride} />
            
            {/* {renderWeather()} */}
          </div>
        </div>
      </div>

      {/* Rating Modal */}
      {ride && (
        <RideRatingModal
          isOpen={showRatingModal}
          onClose={() => setShowRatingModal(false)}
          rideId={ride.id}
          rideTitle={ride.title}
          onRatingComplete={handleRatingComplete}
        />
      )}

      {/* Download Confirmation Modal */}
      {showDownloadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md mx-4 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Download GPX Track</h3>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-amber-900">
                By downloading this route, you acknowledge that this route was provided
                by the ride organizer and has not been verified by Exodus. Exodus makes
                no representations about the accuracy, legality, or safety of this route.
                You are responsible for verifying road access and conditions before riding.
              </p>
            </div>

            <div className="flex gap-3 justify-end">
              <Button
                onClick={() => setShowDownloadModal(false)}
                disabled={downloading}
                variant="outline"
                className="border-gray-300 hover:bg-gray-50"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDownload}
                disabled={downloading}
                className="bg-orange-600 text-white hover:bg-orange-700 flex items-center gap-2"
              >
                {downloading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Downloading...
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4" />
                    I Understand, Download
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RideDetail;
